import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { View } from '@adornis/data-view/db/view.js';
import { FieldConfiguration } from '@adornis/field-management/field-configuration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { LogistikOrderFilter } from '@chancenwerk/db/filter/LogistikOrderFilter.js';
import { NoteFilter } from '@chancenwerk/documentations/api/meeting-notes.js';
import { PartnerFilter } from '@chancenwerk/master-data-management/partner/api/subscribePartnerByFilter.js';
import { ChancenPersonalConfig } from '@chancenwerk/user-configs/entities/ChancenPersonalConfig.js';
import { KpfFilter } from '../../db/filter/KpfFilter.js';

/**
 * @entity ListUIConfig
 */
@Entity()
export class ListUIConfig<T extends typeof AdornisEntity> extends ChancenPersonalConfig {
  public static override _class = 'ListUIConfig';

  @Field(type => View, {
    default: v => v ?? new View({}),
  })
  public view: Maybe<View<T>>;

  public static override getDefault(category: string, key: string) {
    switch (category) {
      case 'list-students':
        return new ListUIConfig({
          category,
          key,
          view: new View({
            _id: A.getGloballyUniqueID(),
            filter: new ChancenwerkUserFilter({}),
            configuredColumns: [
              new FieldConfiguration({ customID: '__actions', columnWidth: '115px' }),
              new FieldConfiguration({ fieldName: 'nachname', columnWidth: '115px' }),
              new FieldConfiguration({ fieldName: 'vorname', columnWidth: '115px' }),
              new FieldConfiguration({ customID: 'studentSchule', columnWidth: '140px' }),
              new FieldConfiguration({ fieldName: 'schoolInformation.jahrgangsstufe', columnWidth: '115px' }),
              new FieldConfiguration({ customID: 'resultInfo', columnWidth: 'max-content' }),
            ],
          }),
        });
      case 'list-persons':
        return new ListUIConfig({
          category,
          key,
          view: new View({
            _id: A.getGloballyUniqueID(),
            filter: new ChancenwerkUserFilter({}),
            configuredColumns: [
              new FieldConfiguration({ fieldName: 'nachname', columnWidth: '115px' }),
              new FieldConfiguration({ fieldName: 'vorname', columnWidth: '115px' }),
              new FieldConfiguration({ customID: '__actions', columnWidth: '41px' }),
              new FieldConfiguration({ fieldName: '_class', columnWidth: '130px' }),
              new FieldConfiguration({ customID: 'chancenID', columnWidth: '86px' }),
              new FieldConfiguration({ customID: 'kpfs', columnWidth: '119px' }),
              new FieldConfiguration({ fieldName: 'schoolInformation.jahrgangsstufe', columnWidth: '115px' }),
              new FieldConfiguration({ fieldName: 'adresse', columnWidth: '223px' }),
              new FieldConfiguration({ fieldName: 'businessAddress', columnWidth: '416px' }),
              new FieldConfiguration({ customID: 'Mitgliedschaftszeitraum', columnWidth: '175px' }),
              new FieldConfiguration({ customID: 'tätigkeiten', columnWidth: '300px' }),
            ],
          }),
        });
      case 'list-kpfs':
        return new ListUIConfig({
          category,
          key,
          view: new View({
            _id: A.getGloballyUniqueID(),
            filter: new KpfFilter({}),
            configuredColumns: [
              new FieldConfiguration({ customID: '__actions', columnWidth: '41px' }),
              new FieldConfiguration({ customID: 'kuerzel', columnWidth: '124px' }),
              new FieldConfiguration({ fieldName: 'school.name', columnWidth: '317px' }),
              new FieldConfiguration({ fieldName: 'school.address', columnWidth: '262px' }),
              new FieldConfiguration({ fieldName: 'produkt.durchfuehrungsTage', columnWidth: '153px' }),
              new FieldConfiguration({ fieldName: 'startDatum', columnWidth: '89px' }),
              new FieldConfiguration({ fieldName: 'endDatum', columnWidth: '89px' }),
              new FieldConfiguration({ fieldName: 'personsStrategisch.email', columnWidth: '201px' }),
              new FieldConfiguration({ fieldName: 'personsOperativ.email', columnWidth: '180px' }),
              new FieldConfiguration({ fieldName: 'personsVerwaltung.email', columnWidth: '196px' }),
              new FieldConfiguration({ fieldName: 'personsBUTOperativ.email', columnWidth: '205px' }),
            ],
          }),
        });
      case 'list-partner':
        return new ListUIConfig({
          category,
          key,
          view: new View({
            _id: A.getGloballyUniqueID(),
            filter: new PartnerFilter({}),
            configuredColumns: [
              new FieldConfiguration({ fieldName: 'kuerzel', columnWidth: '88px' }),
              new FieldConfiguration({ customID: '__actions', columnWidth: '41px' }),
              new FieldConfiguration({ fieldName: '_class', columnWidth: '130px' }),
              new FieldConfiguration({ fieldName: 'name', columnWidth: '257px' }),
              new FieldConfiguration({ fieldName: 'address', columnWidth: '277px' }),
              new FieldConfiguration({ fieldName: 'bundesland', columnWidth: '172px' }),
              new FieldConfiguration({ fieldName: 'productStrings', columnWidth: '121px' }),
              new FieldConfiguration({ fieldName: 'zusammenArbeitStringForDetails', columnWidth: '185px' }),
              new FieldConfiguration({ fieldName: 'lastChanged', columnWidth: '220px' }),
            ],
          }),
        });
      case 'list-notes':
        return new ListUIConfig({
          category,
          key,
          view: new View({
            _id: A.getGloballyUniqueID(),
            filter: new NoteFilter({}),
            configuredColumns: [
              new FieldConfiguration({ customID: 'edit', columnWidth: '35px' }),
              new FieldConfiguration({ fieldName: 'securityLevel', columnWidth: '35px' }),
              new FieldConfiguration({ fieldName: 'occasion', columnWidth: '114px' }),
              new FieldConfiguration({ fieldName: 'category', columnWidth: '114px' }),
              new FieldConfiguration({ fieldName: 'creator', columnWidth: '147px' }),
              new FieldConfiguration({ fieldName: 'year', columnWidth: '78.359375px' }),
              new FieldConfiguration({ fieldName: 'createdAt', columnWidth: '100px' }),
              new FieldConfiguration({ fieldName: 'date', columnWidth: '97px' }),
              new FieldConfiguration({ fieldName: 'kooperationspartner', columnWidth: '259px' }),
              new FieldConfiguration({ fieldName: 'regard', columnWidth: '212px' }),
              new FieldConfiguration({ fieldName: 'protocolLink', columnWidth: '76px' }),
              new FieldConfiguration({ fieldName: 'note', columnWidth: '50px' }),
              new FieldConfiguration({ fieldName: 'gespraechsfuehrungChancenWerk', columnWidth: '196px' }),
            ],
          }),
          configuredColumns: [],
        });
      case 'list-orders':
        return new ListUIConfig({
          category,
          key,
          view: new View({
            _id: A.getGloballyUniqueID(),
            filter: new LogistikOrderFilter({}),
            configuredColumns: [
              new FieldConfiguration({ customID: '_buttons', columnWidth: '41px' }),
              new FieldConfiguration({ customID: '_selection', columnWidth: 'max-content' }),
              new FieldConfiguration({ customID: '_notes', columnWidth: 'max-content' }),
              new FieldConfiguration({ fieldName: 'splitOrderIDs', columnWidth: '41px' }),
              new FieldConfiguration({ fieldName: 'status', columnWidth: '41px' }),
              new FieldConfiguration({ fieldName: 'isExpress', columnWidth: '41px' }),
              new FieldConfiguration({ fieldName: 'shippingDetails.isEmailKnown', columnWidth: '41px' }),
              new FieldConfiguration({ fieldName: 'orderNumber', columnWidth: '115px' }),
              new FieldConfiguration({ customID: 'bestelldatum', columnWidth: '100px' }),
              new FieldConfiguration({ customID: 'frist', columnWidth: '100px' }),
              new FieldConfiguration({ customID: 'client', columnWidth: '100px' }),
              new FieldConfiguration({ customID: 'empfaenger', columnWidth: '100px' }),
              new FieldConfiguration({ customID: 'auftraggeber', columnWidth: '100px' }),
              new FieldConfiguration({ customID: 'bearbeiter', columnWidth: '100px' }),
              new FieldConfiguration({ fieldName: 'isManuallyCreated', columnWidth: '75px' }),
              new FieldConfiguration({ customID: 'productCategory', columnWidth: '100px' }),
            ],
          }),
        });

      case 'list-family-events':
        return new ListUIConfig({
          category,
          key,
          view: new View({
            _id: A.getGloballyUniqueID(),
            filter: new AdornisFilter({}),
            configuredColumns: [
              new FieldConfiguration({ fieldName: 'name', columnWidth: '1fr' }),
              new FieldConfiguration({ fieldName: 'when', columnWidth: '1fr' }),
              new FieldConfiguration({ fieldName: 'acceptedCount', columnWidth: '1fr' }),
            ],
          }),
        });

      case 'list-family-registrations':
        return new ListUIConfig({
          category,
          key,
          view: new View({
            _id: A.getGloballyUniqueID(),
            filter: new AdornisFilter({}),
            configuredColumns: [
              new FieldConfiguration({ fieldName: 'vorname', columnWidth: '1fr' }),
              new FieldConfiguration({ fieldName: 'nachname', columnWidth: '1fr' }),
              new FieldConfiguration({ fieldName: 'email', columnWidth: '1fr' }),
              new FieldConfiguration({ fieldName: 'sprachbedarf', columnWidth: '1fr' }),
            ],
          }),
        });

      case 'kraefte-capacity-request':
        return new ListUIConfig({
          category,
          key,
          view: new View({
            _id: A.getGloballyUniqueID(),
            filter: new AdornisFilter({}),
            configuredColumns: [
              new FieldConfiguration({ fieldName: 'status', columnWidth: 'max-content' }),
              new FieldConfiguration({ fieldName: 'type', columnWidth: 'max-content' }),
              new FieldConfiguration({ fieldName: 'weekdays', columnWidth: 'max-content' }),
              new FieldConfiguration({ fieldName: 'specialSkills', columnWidth: 'max-content' }),
              new FieldConfiguration({ fieldName: 'fromWhen', columnWidth: 'max-content' }),
              new FieldConfiguration({ fieldName: 'createdBy.vorname', columnWidth: 'max-content' }),
              new FieldConfiguration({ fieldName: 'createdBy.nachname', columnWidth: 'max-content' }),
            ],
          }),
        });

      case 'list-markt':
        return new ListUIConfig({
          category,
          key,
          view: new View({
            _id: A.getGloballyUniqueID(),
            filter: new AdornisFilter({}),
            configuredColumns: [
              new FieldConfiguration({ fieldName: '_class', columnWidth: '1fr' }),
              new FieldConfiguration({ fieldName: 'name', columnWidth: '1fr' }),
              new FieldConfiguration({ fieldName: 'kuerzel', columnWidth: '1fr' }),
              new FieldConfiguration({ fieldName: 'parent.name', columnWidth: '1fr' }),
              new FieldConfiguration({ customID: 'status-fachbereich-akquise', columnWidth: '1fr' }),
              new FieldConfiguration({ customID: 'status-veranstaltungs-akquise', columnWidth: '1fr' }),
            ],
          }),
        });

      default:
        throw new Error('Tried to get default. But key ' + category + ' has no default');
    }
  }
}
